/* import __COLOCATED_TEMPLATE__ from './pane.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { useResource } from 'ember-resources';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import { type ChildAction } from 'embercom/objects/inbox/command-k/action';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  type TriggerWorkflowConnectorAction,
  type TriggerWorkflowConnectorActionPart,
} from 'embercom/objects/inbox/command-k/trigger-workflow-connector-action';
import QuickSearchDataMultiple from 'embercom/components/inbox2/common/quick-search/quick-search-data-multiple-resource';
import type CommandKService from 'embercom/services/command-k';
import { inject as service } from '@ember/service';
import type QuickSearch from 'embercom/services/quick-search';
import { SearchableType } from 'embercom/models/data/inbox/searchable-types';
import type Session from 'embercom/services/session';
import { type CommandKPaneComponentArgs } from 'embercom/lib/inbox2/command-k-types';

interface Signature {
  Args: CommandKPaneComponentArgs<
    TriggerWorkflowConnectorAction | TriggerWorkflowConnectorActionPart
  >;
}

const SEARCH_RESULTS_SIZE = 10;

export default class TriggerWorkflowConnectorActionPaneComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare commandK: CommandKService;
  @service declare quickSearch: QuickSearch;

  @tracked query = '';
  @tracked size = SEARCH_RESULTS_SIZE;

  selection = useResource(this, NavigableSelection, () => ({
    items: this.items,
    isCommandKVisible: this.commandK.isVisible,
  }));

  quickSearchData = useResource(this, QuickSearchDataMultiple, () => ({
    types: this.args.actionItem.searchableTypes,
    query: this.query,
    size: this.size,
    currentSearchContext: this.args.actionItem.searchableContext,
    loadSampleData: true,
    onSearchReturn: this.onQuickSearchReturn,
  }));

  get items(): Array<ChildAction> {
    let data = this.quickSearchData.results[SearchableType.WorkflowConnectorAction] ?? {
      total: 0,
      results: [],
    };

    return this.args.actionItem.buildResults({
      [SearchableType.WorkflowConnectorAction]: {
        total: data.total,
        results: data.results,
      },
    });
  }

  @action search(query: string) {
    this.query = query;
  }

  @action onQuickSearchReturn() {
    if (this.selection.items.length) {
      this.selection.select(this.selection.items[0]);
    }

    this.commandK.trackSearch({
      query: this.query,
      number_of_results: this.items.length,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CommandK::TriggerWorkflowConnectorAction::Pane': typeof TriggerWorkflowConnectorActionPaneComponent;
    'inbox2/command-k/trigger-workflow-connector-action/pane': typeof TriggerWorkflowConnectorActionPaneComponent;
  }
}
